import { AxiosError } from 'axios';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import Filter from 'models/api/filter';
import Table from 'models/api/table';
import DataGridProp from 'models/dataGridProp';
import Id from 'models/id';
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Column, SortDirection } from 'react-data-grid';
import {
    MutationFunction,
    QueryClient,
    useMutation,
    UseMutationResult,
    useQuery,
    UseQueryResult,
} from 'react-query';
import { useErrorState } from './ErrorProvider';
import { useGridFilterUrlContext } from './GridFilterUrlProvider';
import { usePendingState } from './PendingProvider';
import { DEFAULT_ERROR_TEXT } from 'utils/consts';

export type TableData<T extends DataGridProp> = Table<T> & { isUserEditable?: boolean };

export type ColumnType<T extends DataGridProp, D = unknown> = Column<T, D> & {
    visible?: boolean;
    order?: number;
    draggable?: boolean;
    clickDisable?: boolean;
    notHidden?: boolean;
    direction?: SortDirection;
    columnsForSorting?: {
        key: string;
        name: string;
    }[];
    disableTooltip?: boolean;
    tooltipText?: string;
    tooltipAlwaysShow?: boolean;
};

export type WorkplaceStateContextType<
    TableType extends DataGridProp = DataGridProp,
    ItemType extends Id = TableType,
    ChangeRequestType = ItemType,
    AddRequestType = ItemType,
    DeleteRequestType = number | string,
> = {
    tableData: TableData<TableType>;
    setTableData: Dispatch<SetStateAction<TableData<TableType>>>;
    currentItem: ItemType | null;
    selectedItem: TableType | null;
    setSelectedItem: Dispatch<SetStateAction<TableType | null>>;
    fetchCurrentItem: () => void;
    buttons?: (JSX.Element | null)[];
    deleteItemMutation: UseMutationResult<unknown, unknown, DeleteRequestType>;
    changeItemMutation: UseMutationResult<unknown, unknown, ChangeRequestType>;
    addItemMutation: UseMutationResult<unknown, unknown, AddRequestType>;
    fetchQuery: UseQueryResult<Table<TableType>>;
    processing: boolean;
    handleError: (error?: AxiosErrorResponse) => void;
    loading: boolean;
    setLoading: Dispatch<boolean>;
};

export type WorkplaceStateProviderProps<
    TableType extends DataGridProp,
    ItemType extends Id = TableType,
    FilterType extends Filter = Filter<TableType>,
    ChangeRequestType = ItemType,
    AddRequestType = ItemType,
    DeleteRequestType = number | string,
> = {
    fetchCurrentItemQueryFn?: (
        id: number | string,
        currentItem: ItemType | TableType
    ) => Promise<ItemType>;
    fetchQueryFn: (filter: FilterType) => Promise<Table<TableType>>;
    addMutationFn?: MutationFunction<unknown, AddRequestType>;
    changeMutationFn?: MutationFunction<unknown, ChangeRequestType>;
    deleteMutationFn?: MutationFunction<unknown, DeleteRequestType>;
    queryKey: string[];
    queryClient: QueryClient;
    buttons?: (JSX.Element | null)[];
    fetchFormat?: (data: any) => TableData<TableType>;
    showModal?: () => void;
    rowClass?: (row: TableType) => string | undefined;
    canFetchData?: boolean;
    refetchOnLanguageChange?: boolean;
};

const initialTableData: TableData<DataGridProp> = {
    data: [],
    rowsCount: 0,
    isUserEditable: false,
};

const WorkplaceStateContext = React.createContext<
    WorkplaceStateContextType<any, any, any, any, any>
>({} as WorkplaceStateContextType);

const WorkplaceStateProvider = <
    TableType extends DataGridProp,
    ItemType extends Id = TableType,
    FilterType extends Filter = Filter<TableType>,
    ChangeRequestType = ItemType,
    AddRequestType = ItemType,
    DeleteRequestType = number | string,
>({
    children,
    fetchCurrentItemQueryFn,
    fetchQueryFn,
    fetchFormat,
    addMutationFn = () => {
        throw new Error('Add function not provided!');
    },
    changeMutationFn = () => {
        throw new Error('Change function not provided!');
    },
    deleteMutationFn = () => {
        throw new Error('Delete function not provided!');
    },
    showModal,
    queryKey,
    queryClient,
    buttons,
    canFetchData = true,
    refetchOnLanguageChange = false,
}: React.PropsWithChildren<
    WorkplaceStateProviderProps<
        TableType,
        ItemType,
        FilterType,
        ChangeRequestType,
        AddRequestType,
        DeleteRequestType
    >
>) => {
    const { setPending } = usePendingState();

    const { filter, setFilter } = useGridFilterUrlContext<FilterType>();
    const [tableData, setTableData] = useState<TableData<DataGridProp>>(initialTableData);

    const [selectedItem, setSelectedItem] = useState<TableType | null>(null);
    const [currentItem, setCurrentItem] = useState<ItemType | null>(null);

    const [loading, setLoading] = useState(true);

    const invalidateQuery = () => {
        queryClient.invalidateQueries([queryKey]);
    };

    const fetchQuery = useQuery([queryKey, filter, canFetchData], () => fetchQueryFn(filter), {
        onSuccess: (data) => {
            if (fetchFormat) {
                data = fetchFormat(data);
            }
            setTableData(data);
            setLoading(false);
        },
        onError: (err: AxiosError<AxiosErrorResponse>) => {
            setTableData(initialTableData);
            errorRequestHandling(err);
            setLoading(false);
        },
        enabled: canFetchData,
    });

    // Для работы повторный поиска с тем же фильтром
    useEffect(() => {
        setLoading(true);
        canFetchData && fetchQuery.refetch();
    }, [filter]);

    const fetchCurrentItemQuery = useQuery(
        [`${queryKey}-current-item`, selectedItem?.id],
        () => {
            return fetchCurrentItemQueryFn &&
                selectedItem?.id !== null &&
                selectedItem?.id !== undefined
                ? fetchCurrentItemQueryFn(selectedItem.id, selectedItem)
                : null;
        },
        {
            onSuccess: (data) => {
                data && setCurrentItem(data);
                showModal && showModal();
            },
            onError: (err: AxiosError<AxiosErrorResponse>) => errorRequestHandling(err),
            initialData: null,
            refetchOnMount: false,
        }
    );

    const deleteItemMutation = useMutation(deleteMutationFn, {
        onSuccess: () => {
            const pageNum = filter.pageNum;
            if (pageNum && pageNum > 1 && tableData?.data?.length === 1) {
                setFilter((prevState) => ({
                    ...prevState,
                    pageNum: pageNum - 1,
                }));
            } else {
                invalidateQuery();
            }
        },
        onError: (err: AxiosError<AxiosErrorResponse>) => errorRequestHandling(err),
    }) as UseMutationResult<unknown, unknown, DeleteRequestType>;

    const changeItemMutation = useMutation(changeMutationFn, {
        onSuccess: invalidateQuery,
        onError: (err: AxiosError<AxiosErrorResponse>) => errorRequestHandling(err),
    });

    const addItemMutation = useMutation(addMutationFn, {
        onSuccess: invalidateQuery,
        onError: (err: AxiosError<AxiosErrorResponse>) => errorRequestHandling(err),
    });

    const { handleError, handleRequestError } = useErrorState();

    const errorHandling = (error?: AxiosErrorResponse) =>
        handleError(
            error ? error.errorData as string : DEFAULT_ERROR_TEXT
        );

    const errorRequestHandling = (error?: AxiosError<AxiosErrorResponse>) =>
        handleRequestError(error);

    const processing =
        deleteItemMutation.isLoading ||
        changeItemMutation.isLoading ||
        addItemMutation.isLoading ||
        fetchCurrentItemQuery.isFetching;

    useEffect(() => setPending(processing), [processing]);

    return (
        <WorkplaceStateContext.Provider
            value={{
                tableData,
                setTableData,
                selectedItem,
                currentItem,
                setSelectedItem: (props) => {
                    setCurrentItem(null);
                    setSelectedItem(props);
                },
                fetchCurrentItem: fetchCurrentItemQuery.refetch,
                buttons,
                deleteItemMutation,
                changeItemMutation,
                addItemMutation,
                fetchQuery,
                processing: processing,
                handleError: errorHandling,
                loading,
                setLoading,
            }}
        >
            {children}
        </WorkplaceStateContext.Provider>
    );
};

const useWorkplaceState = <
    TableType extends DataGridProp,
    ItemType extends Id = TableType,
    ChangeRequestType = ItemType,
    AddRequestType = ItemType,
    DeleteRequestType = number | string,
>() =>
    useContext<
        WorkplaceStateContextType<
            TableType,
            ItemType,
            ChangeRequestType,
            AddRequestType,
            DeleteRequestType
        >
    >(WorkplaceStateContext);

export { WorkplaceStateProvider, useWorkplaceState };
